import { Edit } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type {
  DataGridProProps as DataGridProps,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionButtonLink } from '~/components/button';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { Link } from '~/components/link';
import { useOrderByParams } from '~/hooks/order-by';
import AddRoleIllustration from '~/images/illustrations/add-role.svg';
import type { RoleList__Role } from '../RoleList.generated';
import { RoleMenu } from './RoleMenu';

export interface RoleTableProps extends Omit<DataGridProps, 'columns'> {
  loading: boolean;
  rows: readonly RoleList__Role[];
}

export const RoleTable = ({ loading, ...props }: RoleTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));

  const NoRowsOverlay = () => (
    <EmptyState
      illustration={AddRoleIllustration}
      description={t('pages.roles.list.empty_state.description')}
      header={t('pages.roles.list.empty_state.header')}
    />
  );

  const renderCount = (page: string) =>
    function render({ row, value }: GridRenderCellParams<GridValidRowModel, number>) {
      return (
        <Chip
          color="primary"
          component={Link}
          label={value}
          onClick={(event) => event.stopPropagation()} // Stop from treating like a row click
          sx={{ ml: '5px' }} // Try to center with header better
          to={`${row.id}/${page}`}
        />
      );
    };

  const columns: GridColDef<RoleList__Role>[] = [
    { field: 'name', flex: 0.5, minWidth: isSmallAndUp ? 180 : 0 },
    {
      field: 'userCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.users'),
      minWidth: 125,
      renderCell: renderCount('users'),
      sortable: false,
    },
    {
      field: 'deviceCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('common.labels.devices'),
      minWidth: 125,
      renderCell: renderCount('devices'),
      sortable: false,
    },
    {
      field: 'showFolderCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.show_folders'),
      minWidth: 125,
      renderCell: renderCount('show-folders'),
      sortable: false,
    },
    {
      field: 'playlistFolderCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.playlist_folders'),
      minWidth: 125,
      renderCell: renderCount('playlist-folders'),
      sortable: false,
    },
    {
      field: 'dataSourceCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.data_sources'),
      minWidth: 125,
      renderCell: renderCount('data-sources'),
      sortable: false,
    },
    {
      field: 'designFolderCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.design_folders'),
      minWidth: 125,
      renderCell: renderCount('design-folders'),
      sortable: false,
    },
    {
      field: 'contentFolderCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.content_folders'),
      minWidth: 125,
      renderCell: renderCount('content-folders'),
      sortable: false,
    },
    {
      field: 'eventTypeCount',
      align: 'center',
      headerAlign: 'center',
      headerName: t('pages.roles.list.columns.event_types'),
      minWidth: 125,
      renderCell: renderCount('event-types'),
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => [
        <ActionButtonLink
          Icon={Edit}
          key={0}
          title={t('common.actions.edit')}
          to={String(row.id)}
        />,
        <RoleMenu key={1} networkRole={row} />,
      ],
    },
  ];

  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      contentFolderCount: !isLargeAndDown,
      dataSourceCount: !isLargeAndDown,
      designFolderCount: !isLargeAndDown,
      deviceCount: !isSmallAndDown,
      eventTypeCount: !isLargeAndDown,
      playlistFolderCount: !isSmallAndDown,
      showFolderCount: !isSmallAndDown,
      userCount: !isSmallAndDown,
    }));
  }, [isLargeAndDown, isSmallAndDown]);

  return (
    <ScrollableDataGrid
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      loading={loading}
      onRowClick={({ row }: GridRowParams<RoleList__Role>) => navigate(String(row.id))}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      {...props}
    />
  );
};
